import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The bridge's altar (docs/sendas/paladin.md, chapter 3; §4, «El aljibe del puente»): a stone bridge of one arch over the
 * rift between two plates of the shell, the fourth altar in its middle over a cistern carved in the arch. One board for
 * the three acts, so the place is learned:
 *
 * - The bridge runs along y (x right, y down): its deck from −`ARCH_HEAD` to +`ARCH_HEAD`, `DECK_HALF` across, wider in
 *   the middle round the cistern (`PLATFORM`); past each head the stairs go down to the plates, `STAIR_HALF` across, to
 *   their foot at ±`ARCH_FOOT`. Everything else is the rift (the board's rocks: nothing walks there). The lower end
 *   (+y) is Ser Marit's; the upper end (−y), Ser Varo's and his knights'.
 * - The cistern is the middle cell (a rock: the altar stands in it, on its pillar). Beside it, east and west, the two
 *   places from which the paladin's aura (3500) reaches the keepers of both ends.
 * - Four levers round the cistern: the lower gutter's gate (east of it, toward the lower end) and the upper gutter's
 *   (west, toward the upper end); the lower gargoyle's chain (west, lower side) and the upper's (east, upper side).
 *   From beside the altar two of them are a step away, the other two a few.
 * - The gutters run along the parapets from each head to the cistern: the lower one on the east side, the upper on the
 *   west. A nightmare standing in one stops the rain that comes down it.
 *
 * Integers only. The seed picks the side the rain falls on first and the bands' order: the server builds the same.
 */

export const ARCH_RADIUS = 11;
/** The deck's ends (|y|): where the stairs begin. */
export const ARCH_HEAD = 3400;
/** The stairs' foot (|y|): where the bands set foot on them. */
export const ARCH_FOOT = 8660;
export const DECK_HALF = 1500;
export const STAIR_HALF = 1000;
/** The deck's round middle about the cistern. */
export const PLATFORM = 2300;

/** Where a band comes from: the lower end (0), the upper (1), both (2). */
export type ArchSide = 0 | 1 | 2;

export interface ArchLayout {
  rocks: Hex[];
  /** Where the keepers stand: Ser Marit at the lower end, Ser Varo and his two knights at the upper. */
  keeps: Record<string, { x: number; y: number }>;
  /** Beside the altar, east and west: from here the aura reaches both ends. */
  middle: { x: number; y: number }[];
  /** The levers: the lower gutter's gate, the upper's; the lower gargoyle's chain, the upper's. */
  levers: { x: number; y: number }[];
  /** The gutters (a box each, lower then upper) and where a nightmare stands to stop one. */
  gutters: { x0: number; x1: number; y0: number; y1: number; stand: { x: number; y: number } }[];
  /** The side the heavy rain falls on first (0 the lower half, 1 the upper). */
  rain: 0 | 1;
  /** The bands' sides in order, enough for every act. */
  sides: ArchSide[];
  /** Where the hero starts (by Marit, at the lower end). */
  start: { x: number; y: number };
}

/** Whether a world point is on the bridge (deck or stairs). */
export const archFloor = (x: number, y: number): boolean => {
  const ay = Math.abs(y);
  const ax = Math.abs(x);
  if (ay > ARCH_FOOT + 200) return false;
  if (ay > ARCH_HEAD + 100) return ax <= STAIR_HALF;
  if (ax <= DECK_HALF) return true;
  return x * x + y * y <= PLATFORM * PLATFORM;
};

/** The stairs a point is on (0 the lower, 1 the upper; −1 the deck or off it). */
export const archStairs = (y: number): number => (y > ARCH_HEAD ? 0 : y < -ARCH_HEAD ? 1 : -1);

const build = (seed: number): ArchLayout => {
  const rng = seedRng(deriveSeed(seed, 0xa4c4));
  const R = ARCH_RADIUS;
  const rocks: Hex[] = [];
  for (let r = -R - 2; r <= R + 2; r++)
    for (let q = -R - 2; q <= R + 2; q++) {
      const d = hexLen(q, r);
      if (d > R + 2) continue;
      if (d > R) {
        rocks.push({ q, r });
        continue;
      }
      const w = hexToWorld(q, r);
      // The cistern (the middle cell) and the rift.
      if ((q === 0 && r === 0) || !archFloor(w.x, w.y)) rocks.push({ q, r });
    }
  // The bands: they take turns by end, and now and then both come at once (never the first two).
  const sides: ArchSide[] = [];
  let last: 0 | 1 = rngInt(rng, 2) === 0 ? 0 : 1;
  for (let i = 0; i < 40; i++) {
    if (i >= 2 && rngInt(rng, 3) === 0 && sides[i - 1] !== 2) {
      sides.push(2);
      continue;
    }
    last = last === 0 ? 1 : 0;
    sides.push(last);
  }
  return {
    rocks,
    keeps: {
      marit: { x: 0, y: 2900 },
      varo: { x: 0, y: -3100 },
      orderKnight: { x: -500, y: -2750 },
      orderKnight2: { x: 500, y: -2750 },
    },
    middle: [
      { x: 1000, y: 0 },
      { x: -1000, y: 0 },
    ],
    levers: [
      { x: 1300, y: 1100 },
      { x: -1300, y: -1100 },
      { x: -1300, y: 1100 },
      { x: 1300, y: -1100 },
    ],
    gutters: [
      { x0: 900, x1: 1800, y0: 1400, y1: ARCH_HEAD + 300, stand: { x: 1500, y: 2598 } },
      { x0: -1800, x1: -900, y0: -ARCH_HEAD - 300, y1: -1400, stand: { x: -1500, y: -2598 } },
    ],
    rain: rngInt(rng, 2) === 0 ? 0 : 1,
    sides,
    start: { x: 600, y: 2300 },
  };
};

const CACHE = new Map<number, ArchLayout>();

/** The bridge for a fight's seed. */
export const archLayout = (seed: number): ArchLayout => {
  const hit = CACHE.get(seed);
  if (hit) return hit;
  const L = build(seed);
  if (CACHE.size > 64) CACHE.clear();
  CACHE.set(seed, L);
  return L;
};
